<script setup lang="ts">
import { ref } from "vue";
import { usePanels } from "@/composables/usePanels";
import { useAuth } from "@/composables/useAuth";
import PanelSettingsEditor from "./PanelSettingsEditor.vue";

defineOptions({
  name: "AddPanelModal",
});

const emit = defineEmits(["close"]);
const { createPanel } = usePanels();
const { user } = useAuth();

const showSettingsEditor = ref(true);
const settings = ref({
  title: "",
  instructions: "",
  agentType: "",
  schedule: "",
});
const isCreating = ref(false);
const errorMessage = ref("");

function handleSettingsUpdate(newSettings: {
  title: string;
  instructions: string;
  agentType?: string;
  schedule?: string | { enabled?: boolean; humanText?: string; timezone?: string; cron?: string };
}) {
  if (!user.value?.homeAccountId) {
    errorMessage.value = "Missing user information.";
    return;
  }
  isCreating.value = true;
  errorMessage.value = "";
  // Map schedule (string or object) to schedule object matching Panel interface
  let schedule: { enabled: boolean; humanText: string; timezone: string; cron: string } | undefined = undefined;
  const schedInput = newSettings.schedule;
  if (typeof schedInput === "string") {
    if (schedInput.trim()) {
      schedule = {
        enabled: true,
        humanText: schedInput.trim(),
        timezone: "UTC",
        cron: ""
      };
    }
  } else if (schedInput && typeof schedInput === "object") {
    // If editor passed an object, prefer its fields
    const humanText = (schedInput.humanText || "").toString();
    if (humanText.trim()) {
      schedule = {
        enabled: !!schedInput.enabled,
        humanText: humanText.trim(),
        timezone: schedInput.timezone || "UTC",
        cron: schedInput.cron || ""
      };
    }
  }
  createPanel({
    title: newSettings.title,
    instructions: newSettings.instructions,
    agentType: newSettings.agentType,
    schedule,
  })
    .then(() => {
      emit("close");
    })
    .catch((error: unknown) => {
      if (error instanceof Error) {
        errorMessage.value = `Failed to create panel: ${error.message}`;
      } else {
        errorMessage.value = "Failed to create panel: An unknown error occurred";
      }
    })
    .finally(() => {
      isCreating.value = false;
    });
}
function handleCancel() {
  emit("close");
}
</script>

<template>
  <div class="modal-overlay" @click.self="handleCancel">
    <div class="modal-content">
      <h2>Add New Panel</h2>
      <PanelSettingsEditor :visible="showSettingsEditor" :initialSettings="settings" @update="handleSettingsUpdate"
        @cancel="handleCancel" />
      <div v-if="isCreating">Creating panel...</div>
      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      <button @click="handleCancel" class="close-button">×</button>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

h2 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #333;
}

.error-message {
  color: #dc3545;
  /* Bootstrap danger color */
  background-color: #f8d7da;
  /* Light red background */
  border: 1px solid #f5c6cb;
  /* Red border */
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}

.error-message p {
  margin: 0 0 0.5rem 0;
}
</style>
